import { useState } from "react";
import { Form, Input, Button, Upload, message, Card, Switch } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { blogAPI } from "../services/api";
import "../styles/blog-form.css";

const { TextArea } = Input;

export default function BlogForm({ onSuccess, initialData = null }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initialData?.featured_image || null);

  const handleImageChange = (info) => {
    if (info.file.status === 'removed') {
      setImageFile(null);
      setImagePreview(null);
      return;
    }

    const file = info.file.originFileObj || info.file;
    if (file) {
      setImageFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('title', values.title);
      formData.append('content', values.content);
      formData.append('excerpt', values.excerpt || '');
      formData.append('author_name', values.author_name || '');
      formData.append('author_email', values.author_email || '');
      formData.append('status', values.status || 'draft');
      formData.append('is_featured', values.is_featured || false);

      if (imageFile) {
        formData.append('featured_image', imageFile);
      }

      let response;
      if (initialData) {
        response = await blogAPI.update(initialData.id, formData);
        message.success('Blog updated successfully!');
      } else {
        response = await blogAPI.create(formData);
        message.success('Blog created successfully!');
      }

      form.resetFields();
      setImageFile(null);
      setImagePreview(null);

      if (onSuccess) {
        onSuccess(response.data.data);
      }
    } catch (error) {
      console.error('Blog submission error:', error);
      message.error(error.response?.data?.message || 'Failed to save blog');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="blog-form-card">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={initialData || {
          status: 'draft',
          is_featured: false
        }}
      >
        <Form.Item
          name="title"
          label="Blog Title"
          rules={[{ required: true, message: 'Please enter blog title' }]}
        >
          <Input placeholder="Enter blog title" size="large" />
        </Form.Item>

        <Form.Item
          name="excerpt"
          label="Short Excerpt/Summary"
          tooltip="Brief summary that appears on blog listing page"
        >
          <TextArea 
            rows={3} 
            placeholder="Write a short summary of your blog post..."
            maxLength={300}
            showCount
          />
        </Form.Item>

        <Form.Item
          name="content"
          label="Blog Content"
          rules={[{ required: true, message: 'Please enter blog content' }]}
          tooltip="Write your full blog post content here"
        >
          <TextArea 
            rows={12} 
            placeholder="Write your blog post content here..."
          />
        </Form.Item>

        <Form.Item
          name="featured_image"
          label="Featured Image"
          tooltip="Upload a featured image for your blog post"
        >
          <Upload
            name="featured_image"
            listType="picture-card"
            showUploadList={false}
            beforeUpload={() => false} 
            onChange={handleImageChange}
            accept="image/*"
          >
            {imagePreview ? (
              <div className="image-preview-wrapper">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <div className="image-preview-overlay">
                  <UploadOutlined /> Change
                </div>
              </div>
            ) : (
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
              </div>
            )}
          </Upload>
        </Form.Item>

        <div className="blog-form-row">
          <Form.Item
            name="author_name"
            label="Author Name"
            style={{ flex: 1, marginRight: '1rem' }}
          >
            <Input placeholder="Your name" />
          </Form.Item>

          <Form.Item
            name="author_email"
            label="Author Email"
            style={{ flex: 1 }}
            rules={[
              { type: 'email', message: 'Please enter a valid email' }
            ]}
          >
            <Input placeholder="your.email@example.com" />
          </Form.Item>
        </div>

        <div className="blog-form-row">
          <Form.Item
            name="status"
            label="Status"
            style={{ flex: 1, marginRight: '1rem' }}
          >
            <Input placeholder="draft or published" />
          </Form.Item>

          <Form.Item
            name="is_featured"
            label="Featured"
            valuePropName="checked"
            style={{ flex: 1 }}
          >
            <Switch />
          </Form.Item>
        </div>

        <Form.Item>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large"
            loading={loading}
            block
          >
            {initialData ? 'Update Blog' : 'Create Blog'}
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
}

